/**
 * Fullscreen image viewer for the product gallery.
 *
 * Built on <dialog> + showModal(), which gives the top layer, the focus trap,
 * Escape-to-close and focus restoration for free — all of it fiddly and easy
 * to get subtly wrong by hand.
 *
 * Full-size sources are held in data-src and only fetched when the lightbox
 * first opens, so a 2048px copy of every photo is never on the critical path
 * of a page most visitors never zoom on.
 */
class ProductLightbox extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('[data-gallery-lightbox]');
    if (!this.dialog) return;

    this.slides = [...this.dialog.querySelectorAll('.lightbox-slide')];
    this.counter = this.dialog.querySelector('[data-lightbox-counter]');
    this.index = 0;

    this.onClick = this.onClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onClose = this.onClose.bind(this);
    this.onZoom = this.onZoom.bind(this);

    this.addEventListener('click', this.onClick);
    this.dialog.addEventListener('keydown', this.onKeydown);
    document.addEventListener('gallery:zoom', this.onZoom);

    // Swipe. The slides are shown and hidden rather than laid out in a track,
    // so there is no native scrolling to inherit — without this the arrows are
    // the only way through, which on a phone is the one input nobody reaches
    // for.
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    const stage = this.dialog.querySelector('[data-lightbox-stage]') || this.dialog;
    this.onPointerMove = this.onPointerMove.bind(this);
    stage.addEventListener('pointerdown', this.onPointerDown);
    stage.addEventListener('pointermove', this.onPointerMove);
    stage.addEventListener('pointerup', this.onPointerUp);
    stage.addEventListener('pointercancel', () => { this.swipe = null; });
    this.stage = stage;

    // Cleanup watches the `open` attribute rather than the `close` event.
    // Every way out — the close button, the backdrop, Escape, close() — must
    // clear that attribute, whereas the event is not dispatched at all in
    // some browsers, which left the page scroll-locked after closing.
    this.observer = new MutationObserver(() => {
      if (!this.dialog.hasAttribute('open')) this.onClose();
    });
    this.observer.observe(this.dialog, { attributes: true, attributeFilter: ['open'] });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.dialog?.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('gallery:zoom', this.onZoom);
    this.stage?.removeEventListener('pointerdown', this.onPointerDown);
    this.stage?.removeEventListener('pointermove', this.onPointerMove);
    this.stage?.removeEventListener('pointerup', this.onPointerUp);
    this.observer?.disconnect();
  }

  /** The gallery says which slide it was showing when zoom was pressed. */
  onZoom(event) {
    this.open(event.detail?.index || 0);
  }

  onClick(event) {
    if (event.target.closest('[data-lightbox-close]')) {
      this.dialog.close();
      return;
    }
    if (event.target.closest('[data-gallery-prev]')) {
      this.show(this.index - 1);
      return;
    }
    if (event.target.closest('[data-gallery-next]')) {
      this.show(this.index + 1);
      return;
    }

    // A click landing on the dialog itself is a click on the backdrop area
    // around the image, which should dismiss — unless it is the tail of a
    // swipe, which ends over the stage and is not a dismissal.
    if (this.swiped) return;
    if (event.target === this.dialog || event.target.closest('[data-lightbox-stage]') === event.target) {
      this.dialog.close();
    }
  }

  onPointerDown(event) {
    // Only a single finger or the primary mouse button. A second pointer is a
    // pinch, and hijacking that would take zoom away from the one place in the
    // theme built for it.
    if (!event.isPrimary) {
      this.swipe = null;
      return;
    }
    this.swipe = { x: event.clientX, y: event.clientY };
  }

  /**
   * Acts as soon as the threshold is crossed rather than waiting for the
   * finger to lift. Belt and braces alongside `touch-action` in the CSS: if
   * anything ever claims the gesture, `pointercancel` arrives instead of
   * `pointerup`, and a handler that only listened for the lift would never
   * fire at all.
   */
  onPointerMove(event) {
    if (event.isPrimary) this.tryAdvance(event.clientX, event.clientY);
  }

  onPointerUp(event) {
    // Only does anything when the pointer never crossed the threshold while
    // moving — a slow, short drag that finished on the lift.
    this.tryAdvance(event.clientX, event.clientY);
    this.swipe = null;
  }

  /** Moves a slide if the travel so far reads as a horizontal swipe. */
  tryAdvance(x, y) {
    const start = this.swipe;
    if (!start) return;

    const dx = x - start.x;
    const dy = y - start.y;

    // A mostly-vertical drag is a scroll or a dismiss gesture, and 45px is far
    // enough that a tap with a shaky thumb does not change the slide.
    if (Math.abs(dx) < 45 || Math.abs(dx) < Math.abs(dy)) return;

    // Consumed, so the lift cannot move a second slide.
    this.swipe = null;

    // Marks the gesture so the click that follows does not also close the
    // lightbox — a swipe ending over the stage otherwise reads as a backdrop
    // tap.
    this.swiped = true;
    setTimeout(() => { this.swiped = false; }, 0);

    this.show(dx < 0 ? this.index + 1 : this.index - 1);
  }

  onKeydown(event) {
    if (event.key === 'ArrowLeft') this.show(this.index - 1);
    if (event.key === 'ArrowRight') this.show(this.index + 1);
  }

  onClose() {
    document.body.classList.remove('is-lightbox-open');
    for (const slide of this.slides) slide.querySelector('video')?.pause();
  }

  open(index) {
    // Page scroll is locked because the dialog covers it entirely.
    document.body.classList.add('is-lightbox-open');
    this.show(index);
    this.dialog.showModal();
  }

  show(index) {
    const count = this.slides.length;
    this.index = ((index % count) + count) % count;

    for (const slide of this.slides) {
      const isActive = Number(slide.dataset.index) === this.index;
      slide.toggleAttribute('hidden', !isActive);

      if (isActive) {
        const img = slide.querySelector('img[data-src]');
        if (img) {
          img.src = img.dataset.src;
          img.removeAttribute('data-src');
        }
      } else {
        slide.querySelector('video')?.pause();
      }
    }

    if (this.counter) this.counter.textContent = `${this.index + 1} / ${count}`;
  }
}

customElements.define('product-lightbox', ProductLightbox);
